import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ProductsApiService from "../../services/ProductsApiService";
import { getLanguage } from "../../utils/Helper";
import styles from "./ShopByColorCategory.module.scss";

// ─── i18n ────────────────────────────────────────────────────────────────────
const t = {
  EN: {
    discover: "Discover",
    shopByColor: "Shop by",
    color: "Color",
    allCategories: "All",
    allColors: "All colors",
    colorLabel: (name) => `Color: ${name}`,
    clearFilters: "✕ Clear filters",
    noProducts: "No products found for these filters.",
    clearFiltersBtn: "Clear filters",
    viewAll: (n) => `View all ${n.toLocaleString()} products →`,
    new: "NEW",
  },
  AR: {
    discover: "اكتشف",
    shopByColor: "تسوق حسب",
    color: "اللون",
    allCategories: "الكل",
    allColors: "كل الألوان",
    colorLabel: (name) => `اللون: ${name}`,
    clearFilters: "✕ مسح الفلاتر",
    noProducts: "لا توجد منتجات لهذه الفلاتر.",
    clearFiltersBtn: "مسح الفلاتر",
    viewAll: (n) => `عرض كل ${n.toLocaleString()} منتج ←`,
    new: "جديد",
  },
};

// ─── helpers ─────────────────────────────────────────────────────────────────
const localName = (obj, lang) =>
  (lang === "AR" ? obj?.nameAr : obj?.nameEn) || obj?.nameEn || "";

const calcDiscount = (price, disc) =>
  price && disc && disc < price
    ? Math.round(((price - disc) / price) * 100)
    : null;

const getMinPrice = (variants = []) => {
  const prices = variants
    .map((v) => v.discountPrice ?? v.price)
    .filter(Boolean);
  return prices.length ? Math.min(...prices) : null;
};

const getOriginalMinPrice = (variants = []) => {
  const prices = variants.map((v) => v.price).filter(Boolean);
  return prices.length ? Math.min(...prices) : null;
};

const getUniqueColors = (variants = []) => {
  const seen = new Set();
  return variants
    .map((v) => v.color)
    .filter((c) => c?.hexCode && !seen.has(c.hexCode) && seen.add(c.hexCode));
};

// ─── SkeletonCard ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className={styles.skeletonCard}>
    <div className={styles.skeletonImg} />
    <div className={styles.skeletonBody}>
      <div
        className={styles.skeletonLine}
        style={{ width: "40%", height: 10 }}
      />
      <div
        className={styles.skeletonLine}
        style={{ width: "75%", height: 13 }}
      />
      <div
        className={styles.skeletonLine}
        style={{ width: "50%", height: 11 }}
      />
    </div>
  </div>
);

// ─── ProductCard ──────────────────────────────────────────────────────────────
const ProductCard = ({ product, lang }) => {
  const [imgError, setImgError] = useState(false);
  const i18n = t[lang] ?? t.EN;
  const isRtl = lang === "AR";

  const minPrice = getMinPrice(product.variants);
  const originalPrice = getOriginalMinPrice(product.variants);
  const discount = calcDiscount(originalPrice, minPrice);
  const colors = getUniqueColors(product.variants);
  const navigate = useNavigate();

  const openProductProfile = (pro) => {
    const collectionName = pro.collection.nameEn
      .toString()
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");
    const catName = pro.category.nameEn
      .toString()
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");
    const subCatName = pro?.subCategory?.nameEn
      ? pro.subCategory.nameEn
          .toString()
          .toLowerCase()
          .trim()
          .split(" ")
          .join("-")
      : "";
      return !subCatName
      ? `/collection/${collectionName}/${catName}/${pro.id}`
      : `/collection/${collectionName}/${catName}/${subCatName}/${pro.id}`;
    // return `/collection/${collectionName}/${catName}/${subCatName}/${pro.id}`;
  };

  return (
    <article
      className={styles.productCard}
      dir={isRtl ? "rtl" : "ltr"}
      onClick={() => navigate(openProductProfile(product))}
    >
      <div className={styles.imageWrap}>
        {imgError || !product.primaryImageUrl ? (
          <div className={styles.imgPlaceholder}>
            <span>🛍️</span>
          </div>
        ) : (
          <img
            src={product.primaryImageUrl}
            alt={localName(product, lang)}
            className={styles.productImage}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        )}

        {discount ? (
          <span className={styles.discountBadge}>-{discount}%</span>
        ) : (
          product.isNew && (
            <span className={`${styles.discountBadge} ${styles.newBadge}`}>
              {i18n.new}
            </span>
          )
        )}
      </div>

      <div className={styles.cardBody}>
        {localName(product.brand, lang) && (
          <span className={styles.brandLabel}>
            {localName(product.brand, lang)}
          </span>
        )}
        <h3 className={styles.productName}>{localName(product, lang)}</h3>

        {colors.length > 0 && (
          <div className={styles.colorStrip}>
            {colors.slice(0, 6).map((c) => (
              <span
                key={c.hexCode}
                className={styles.colorDot}
                style={{ background: c.hexCode }}
                title={localName(c, lang) || c.hexCode}
              />
            ))}
            {colors.length > 6 && (
              <span className={styles.moreColors}>+{colors.length - 6}</span>
            )}
          </div>
        )}

        <div className={styles.priceRow}>
          <span className={styles.priceNow}>
            {minPrice?.toLocaleString("en-EG")} EGP
          </span>
          {discount && (
            <span className={styles.priceWas}>
              {originalPrice?.toLocaleString("en-EG")} EGP
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ShopByColorCategory = ({ limit = 8 }) => {
  const lang = getLanguage();
  const i18n = t[lang] ?? t.EN;
  const isRtl = lang === "AR";
  const navigate = useNavigate();

  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [activeCollection, setActiveCollection] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeColor, setActiveColor] = useState(null);

  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // AbortController ref — cancels in-flight products request on rapid filter change
  const abortRef = useRef(null);
  const tabsRowRef = useRef(null);

  const updateArrows = () => {
    const el = tabsRowRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  const scrollTabs = (dir) => {
    const el = tabsRowRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  useEffect(() => {
    const el = tabsRowRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
    };
  }, [collections]); // eslint-disable-line

  // ── 1. boot: load all collections once ───────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        setLoadingInit(true);
        const data = await ProductsApiService.getFilters();
        const cols = data.collections ?? [];
        setCollections(cols);
        if (cols.length) setActiveCollection(cols[0].id);
      } catch (err) {
        console.error("ShopByColorCategory — init error:", err);
      } finally {
        setLoadingInit(false);
      }
    };
    init();
  }, []);

  // ── 2. collection changes → fetch categories + colors in parallel ─────────
  useEffect(() => {
    if (!activeCollection) return;
    setActiveCategory(null);
    setActiveColor(null);
    setColors([]);

    const fetchMeta = async () => {
      try {
        const [cats, attrs] = await Promise.all([
          ProductsApiService.getCategoriesByCollection(activeCollection),
          ProductsApiService.getColorsAndSizesByFilters({
            collectionId: activeCollection,
          }),
        ]);
        setCategories(cats ?? []);
        setColors(attrs.colors ?? []);
      } catch (err) {
        console.error("ShopByColorCategory — fetchMeta error:", err);
      }
    };
    fetchMeta();
  }, [activeCollection]);

  // ── 3. category changes → re-fetch colors scoped to that category ─────────
  useEffect(() => {
    if (!activeCollection) return;
    setActiveColor(null);

    const fetchColors = async () => {
      try {
        const attrs = await ProductsApiService.getColorsAndSizesByFilters({
          collectionId: activeCollection,
          categoryIds: activeCategory ?? undefined,
        });
        setColors(attrs.colors ?? []);
      } catch (err) {
        console.error("ShopByColorCategory — fetchColors error:", err);
      }
    };
    fetchColors();
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 4. any filter change → fetch products  +  empty fallback ─────────────
  useEffect(() => {
    if (!activeCollection) return;

    // Cancel previous in-flight request
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const params = new URLSearchParams({
          collectionId: activeCollection,
          page: 0,
          limit,
        });
        if (activeCategory) params.append("categoryIds", activeCategory);
        if (activeColor) params.append("colorIds", activeColor);

        const data =
          await ProductsApiService.getProductsListWithFilters(params);
        const fetched = data.products ?? [];

        // ── empty fallback ────────────────────────────────────────────────
        if (fetched.length === 0 && !activeCategory && !activeColor) {
          const currentIdx = collections.findIndex(
            (c) => c.id === activeCollection,
          );
          const nextCol = collections[currentIdx + 1];
          if (nextCol) {
            setActiveCollection(nextCol.id);
            return;
          }
        }
        // ─────────────────────────────────────────────────────────────────

        setProducts(fetched);
        setTotalCount(data.totalCount ?? 0);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("ShopByColorCategory — fetchProducts error:", err);
        }
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [activeCollection, activeCategory, activeColor, limit]);

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleCollectionClick = (id) => setActiveCollection(id);
  const handleCategoryClick = (id) =>
    setActiveCategory((p) => (p === id ? null : id));
  const handleColorClick = (id) =>
    setActiveColor((p) => (p === id ? null : id));
  const clearFilters = () => {
    setActiveCategory(null);
    setActiveColor(null);
  };

  // ── View All handler ──────────────────────────────────────────────────────
  const activeCollectionObj = collections.find(
    (c) => c.id === activeCollection,
  );

  const handleViewAll = () => {
    if (!activeCollectionObj?.nameEn) return;
    const params = new URLSearchParams();
    if (activeCategory) params.set("categoryId", activeCategory);
    if (activeColor) params.set("colorId", activeColor);
    // const qs = params.toString();
    // navigate(`/collection/${activeCollectionObj.nameEn}${qs ? `?${qs}` : ''}`);
    navigate(`/collection/${activeCollectionObj.nameEn}`);
  };

  // ── derived ───────────────────────────────────────────────────────────────
  const selectedColorObj = colors.find((c) => c.id === activeColor);
  const hasFilters = activeCategory || activeColor;
  const selectedColorName = selectedColorObj
    ? localName(selectedColorObj, lang)
    : null;

  // ── boot skeleton ─────────────────────────────────────────────────────────
  if (loadingInit) {
    return (
      <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.inner}>
          <div className={styles.skeletonHeader} />
          <div className={styles.grid}>
            {Array.from({ length: limit }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── full render ───────────────────────────────────────────────────────────
  return (
    <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.inner}>
        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.sectionLabel}>{i18n.discover}</span>
            <h2 className={styles.sectionTitle}>
              {i18n.shopByColor}{" "}
              {selectedColorName ? (
                <span className={styles.colorHighlight}>
                  <span
                    className={styles.titleColorDot}
                    style={{ background: selectedColorObj.hexCode }}
                  />
                  {selectedColorName}
                </span>
              ) : (
                <span className={styles.accentWord}>{i18n.color}</span>
              )}
            </h2>
          </div>
        </div>

        {/* ── Collection tabs ── */}
        {collections.length > 1 && (
          <div className={styles.tabsWrapper}>
            <button
              className={`${styles.tabArrow} ${styles.tabArrowLeft} ${!canScrollLeft ? styles.tabArrowHidden : ""}`}
              onClick={() => scrollTabs(isRtl ? 1 : -1)}
              aria-label="scroll left"
            >
              ‹
            </button>

            <div className={styles.tabsRow} role="tablist" ref={tabsRowRef}>
              {collections.map((col) => (
                <button
                  key={col.id}
                  role="tab"
                  aria-selected={activeCollection === col.id}
                  className={`${styles.collectionTab} ${activeCollection === col.id ? styles.tabActive : ""}`}
                  onClick={() => handleCollectionClick(col.id)}
                >
                  {localName(col, lang)}
                </button>
              ))}
            </div>

            <button
              className={`${styles.tabArrow} ${styles.tabArrowRight} ${!canScrollRight ? styles.tabArrowHidden : ""}`}
              onClick={() => scrollTabs(isRtl ? -1 : 1)}
              aria-label="scroll right"
            >
              ›
            </button>
          </div>
        )}
        {/* ── Category pills ── */}
        {categories.length > 0 && (
          <div className={styles.pillsRow} role="group">
            <button
              className={`${styles.pill} ${!activeCategory ? styles.pillActive : ""}`}
              onClick={() => setActiveCategory(null)}
            >
              {i18n.allCategories}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`${styles.pill} ${activeCategory === cat.id ? styles.pillActive : ""}`}
                onClick={() => handleCategoryClick(cat.id)}
              >
                {localName(cat, lang)}
              </button>
            ))}
          </div>
        )}

        {/* ── Color swatches ── */}
        {colors.length > 0 && (
          <div className={styles.colorsSection}>
            <span className={styles.colorsLabel}>
              {selectedColorName
                ? i18n.colorLabel(selectedColorName)
                : i18n.allColors}
            </span>
            <div className={styles.swatchesRow} role="group">
              {colors.map((color) => (
                <button
                  key={color.id}
                  aria-label={localName(color, lang) || color.hexCode}
                  aria-pressed={activeColor === color.id}
                  className={`${styles.swatch} ${activeColor === color.id ? styles.swatchActive : ""}`}
                  style={{ "--c": color.hexCode }}
                  onClick={() => handleColorClick(color.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Products grid ── */}
        <div
          className={`${styles.grid} ${loadingProducts ? styles.gridLoading : ""}`}
        >
          {loadingProducts ? (
            Array.from({ length: limit }).map((_, i) => (
              <SkeletonCard key={i} />
            ))
          ) : products.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyText}>{i18n.noProducts}</p>
              {hasFilters && (
                <button className={styles.clearBtn} onClick={clearFilters}>
                  {i18n.clearFiltersBtn}
                </button>
              )}
            </div>
          ) : (
            products.map((product) => (
              <ProductCard key={product.id} product={product} lang={lang} />
            ))
          )}
        </div>

        {/* ── View All ── */}
        {!loadingProducts && totalCount > limit && (
          <div className={styles.viewMoreRow}>
            <button className={styles.viewMoreBtn} onClick={handleViewAll}>
              {i18n.viewAll(totalCount)}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ShopByColorCategory;
